import {
  Button,
  Card,
  Form,
  Input,
  message,
  Select,
  Space,
  Typography,
} from 'antd'
import { format } from 'date-fns'
import { ReviewSection, ReviewSectionProps } from 'features/reporting/types'
import { useSensorPayloadStrings } from 'pages/reporting/reports/rug/hooks/useSensorPayloadStrings'
import { RugReportSectionId } from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import { formatInspectionTemperature } from 'pages/reporting/reports/rug/utils/inspectionTemperature'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { SignoffRole, SignoffState } from 'types'

const Text = Typography.Text

const QualificationOptions = [
  'Level II Ultrasonics',
  'ASNT Level III Ultrasonics',
  'Advanced Services Manager',
  'NDT Technology Manager',
]

const REVIEW_ROLE_ORDER = [
  {
    role: SignoffRole.SUPERVISOR,
    field: 'supervisor',
    label: 'Inspection Supervisor',
  },
  {
    role: SignoffRole.DATA_ANALYST,
    field: 'dataAnalyst',
    label: 'Data Analyst',
  },
  {
    role: SignoffRole.REVIEWER,
    field: 'reviewer',
    label: 'Reviewer',
  },
] as const

const OPTIONAL_REVIEW_ROLES = new Set<SignoffRole>([SignoffRole.DATA_ANALYST])

const PageSection = styled(Card)`
  margin-bottom: 16px;
`

const SectionContent = styled.div`
  margin-left: 16px;
`

const DataRow = styled.div`
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
`

type ReviewRoleFieldValues = {
  name?: string
  qualifications?: string
}

type ReviewFormValues = {
  supervisor?: ReviewRoleFieldValues
  dataAnalyst?: ReviewRoleFieldValues
  reviewer?: ReviewRoleFieldValues
}

const ReviewForm = styled(Form<ReviewFormValues>)`
  margin-top: 16px;
`

const getRoleLabel = (role: SignoffRole) => {
  return (
    REVIEW_ROLE_ORDER.find((reviewRole) => reviewRole.role === role)?.label ??
    'Reviewer'
  )
}

export const getReviewEntryForRole = (
  signoff: SignoffState | undefined,
  role: SignoffRole
) => {
  if (role === SignoffRole.SUPERVISOR) return signoff?.supervisorSignoff
  if (role === SignoffRole.DATA_ANALYST) return signoff?.dataAnalystSignoff
  return signoff?.reviewerSignoff
}

const buildReviewFormValues = (
  signoff: SignoffState | undefined
): ReviewFormValues => ({
  supervisor: {
    name: signoff?.supervisorSignoff?.name,
    qualifications: signoff?.supervisorSignoff?.qualifications,
  },
  dataAnalyst: {
    name: signoff?.dataAnalystSignoff?.name,
    qualifications: signoff?.dataAnalystSignoff?.qualifications,
  },
  reviewer: {
    name: signoff?.reviewerSignoff?.name,
    qualifications: signoff?.reviewerSignoff?.qualifications,
  },
})

const getReviewEntryTimestamp = (
  existingEntry: ReturnType<typeof getReviewEntryForRole> | undefined,
  values: ReviewRoleFieldValues | undefined,
  fallbackTimestamp: string
) => {
  const nextName = values?.name ?? ''
  const nextQualifications = values?.qualifications ?? ''

  if (
    existingEntry?.name === nextName &&
    existingEntry?.qualifications === nextQualifications &&
    existingEntry.timestamp
  ) {
    return existingEntry.timestamp
  }

  return fallbackTimestamp
}

export const isReviewRoleRequired = (role: SignoffRole) => {
  return !OPTIONAL_REVIEW_ROLES.has(role)
}

const normalizeReviewRoleValues = (
  values: ReviewRoleFieldValues | undefined
) => ({
  name: values?.name?.trim() ?? '',
  qualifications: values?.qualifications ?? '',
})

export const getReviewEntriesToSave = ({
  signoff,
  values,
  timestamp = new Date().toISOString(),
}: {
  signoff: SignoffState | undefined
  values: ReviewFormValues
  timestamp?: string
}) => {
  const entries = []

  for (const reviewRole of REVIEW_ROLE_ORDER) {
    const existingEntry = getReviewEntryForRole(signoff, reviewRole.role)
    const roleValues = values[reviewRole.field]
    const normalizedValues = normalizeReviewRoleValues(roleValues)
    const hasName = normalizedValues.name !== ''
    const hasQualifications = normalizedValues.qualifications !== ''

    if (!isReviewRoleRequired(reviewRole.role)) {
      if (hasName !== hasQualifications) {
        throw new Error(
          `${reviewRole.label} requires both name and qualifications or can be left blank`
        )
      }

      if (!hasName) {
        continue
      }
    }

    entries.push({
      role: reviewRole.role,
      name: normalizedValues.name,
      qualifications: normalizedValues.qualifications,
      timestamp: getReviewEntryTimestamp(
        existingEntry,
        normalizedValues,
        timestamp
      ),
    })
  }

  return entries
}

const formatDate = (date: string | Date | undefined) => {
  if (!date) return 'Not specified'

  try {
    if (typeof date === 'string') {
      return date // Assume it's already formatted
    }
    return format(date, 'MM/dd/yyyy')
  } catch {
    return 'Invalid date'
  }
}

const formatCalibrationTime = (timeString: string): string => {
  try {
    const date = new Date(timeString)
    if (isNaN(date.getTime())) return timeString
    return format(date, 'MM/dd/yyyy h:mm a')
  } catch {
    return timeString
  }
}

const RenderReact: React.FC<ReviewSectionProps> = ({ previewMode }) => {
  const {
    formData,
    handleSignoff,
    selectedRevisionData,
    isLoading,
    organization,
    unit,
    inspectionMetadata,
    formatMeasurement,
    selectedComponents,
  } = useRugReportOps()
  const [isEditingReviewers, setIsEditingReviewers] = useState(false)
  const [form] = Form.useForm<ReviewFormValues>()

  const selectedFormData = useMemo(
    () =>
      previewMode && selectedRevisionData ? selectedRevisionData : formData,
    [formData, selectedRevisionData, previewMode]
  )

  const {
    mainInfo,
    coverSectionData,
    summarySectionData,
    dataSheetSectionData,
    imagesSectionData,
    signoff,
    revisionMetadata,
  } = selectedFormData

  const supervisorSignDate = signoff?.supervisorSignoff?.timestamp
    ? new Date(signoff.supervisorSignoff.timestamp)
    : undefined

  const dataAnalystSignDate = signoff?.dataAnalystSignoff?.timestamp
    ? new Date(signoff.dataAnalystSignoff.timestamp)
    : undefined

  const reviewerSignDate = signoff?.reviewerSignoff?.timestamp
    ? new Date(signoff.reviewerSignoff.timestamp)
    : undefined

  const resolutionString = summarySectionData.inspectionResolution
    ? `${summarySectionData.inspectionResolution.width}" x ${summarySectionData.inspectionResolution.height}"`
    : 'Not specified'

  const deliverableResolutionString = summarySectionData.deliverableResolution
    ? `${summarySectionData.deliverableResolution.width}" x ${summarySectionData.deliverableResolution.height}"`
    : 'Not specified'

  const { payloadTypesStr, sensorFrequenciesStr, sensorDiametersStr } =
    useSensorPayloadStrings({
      payloads: dataSheetSectionData?.payloads,
      formatMeasurement,
    })

  const inspectionTemperature = formatInspectionTemperature(
    mainInfo.inspectionTemperature
  )

  const sensorAngle = inspectionMetadata?.sensorPayloadMetadata?.angle

  const reviewEntries = [
    signoff?.supervisorSignoff,
    signoff?.dataAnalystSignoff,
    signoff?.reviewerSignoff,
  ].filter(Boolean)
  const hasSavedReviewEntries = reviewEntries.length > 0
  const showReviewEditor =
    !previewMode && (!hasSavedReviewEntries || isEditingReviewers)

  const startEditingReviewers = useCallback(() => {
    setIsEditingReviewers(true)
    form.setFieldsValue(buildReviewFormValues(signoff))
  }, [form, signoff])

  const stopEditingReviewers = useCallback(() => {
    setIsEditingReviewers(false)
    form.resetFields()
  }, [form])

  useEffect(() => {
    if (!showReviewEditor) {
      return
    }

    form.setFieldsValue(buildReviewFormValues(signoff))
  }, [form, showReviewEditor, signoff])

  const handleSaveReviewers = useCallback(async () => {
    try {
      const values = await form.validateFields()
      const entriesToSave = getReviewEntriesToSave({
        signoff,
        values,
      })

      for (const reviewEntry of entriesToSave) {
        await handleSignoff(reviewEntry)
      }

      stopEditingReviewers()

      message.success('Reviewer details saved')
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : 'Please complete all required fields'
      )
    }
  }, [form, handleSignoff, signoff, stopEditingReviewers])

  return (
    <div>
      <PageSection title='Asset Information'>
        <SectionContent>
          <DataRow>
            <Text strong>Customer:</Text>{' '}
            {mainInfo.customerName || organization?.data.name || 'N/A'}
          </DataRow>
          <DataRow>
            <Text strong>Location:</Text> {mainInfo.customerLocation || 'N/A'}
          </DataRow>
          <DataRow>
            <Text strong>Asset Name:</Text>{' '}
            {mainInfo.assetName || unit?.data.name || 'N/A'}
          </DataRow>
          <DataRow>
            <Text strong>Asset ID:</Text> {mainInfo.assetId}
          </DataRow>
          <DataRow>
            <Text strong>Asset Type:</Text> {mainInfo.assetType}
          </DataRow>
          <DataRow>
            <Text strong>Asset Material:</Text> {mainInfo.assetMaterial}
          </DataRow>
          <DataRow>
            <Text strong>Inspection Temperature:</Text> {inspectionTemperature}
          </DataRow>
          {selectedComponents &&
            selectedComponents.map((component) => {
              const axisLocations =
                mainInfo.componentAxisStartLocations?.[component.id]
              return (
                <React.Fragment key={component.id}>
                  <DataRow>
                    <Text strong>
                      X-Axis Start Location ({component.data.name}):
                    </Text>{' '}
                    {axisLocations?.xAxisStartLocation ?? 'Not specified'}
                  </DataRow>
                  <DataRow>
                    <Text strong>
                      Y-Axis Start Location ({component.data.name}):
                    </Text>{' '}
                    {axisLocations?.yAxisStartLocation ?? 'Not specified'}
                  </DataRow>
                </React.Fragment>
              )
            })}
        </SectionContent>
      </PageSection>

      <PageSection title='Inspection Details'>
        <SectionContent>
          <DataRow>
            <Text strong>Inspection Start Date:</Text>{' '}
            {formatDate(coverSectionData.inspectionStartDate)}
          </DataRow>
          <DataRow>
            <Text strong>Inspection End Date:</Text>{' '}
            {formatDate(coverSectionData.inspectionEndDate)}
          </DataRow>
          <DataRow>
            <Text strong>Revision Number:</Text>{' '}
            {revisionMetadata.revisionNumber}
          </DataRow>
          <DataRow>
            <Text strong>Publication Status:</Text>{' '}
            {revisionMetadata.isPublished ? 'Published' : 'Draft'}
          </DataRow>
        </SectionContent>
      </PageSection>

      <PageSection title='Inspection Summary'>
        <SectionContent>
          <DataRow>
            <Text strong>Coverage Scope:</Text>{' '}
            {summarySectionData.coverageScope || 'Not specified'}
          </DataRow>
          <DataRow>
            <Text strong>Inspection Resolution:</Text> {resolutionString}
          </DataRow>
          <DataRow>
            <Text strong>Deliverable Resolution:</Text>{' '}
            {deliverableResolutionString}
          </DataRow>
          <DataRow>
            <Text strong>Corrosion Summary:</Text>
          </DataRow>
          <DataRow style={{ marginLeft: 16 }}>
            {summarySectionData.corrosionBLUF ||
              'No corrosion summary provided'}
          </DataRow>
          {summarySectionData.inspectionSummaryNotes && (
            <>
              <DataRow>
                <Text strong>Additional Notes:</Text>
              </DataRow>
              <DataRow style={{ marginLeft: 16 }}>
                {summarySectionData.inspectionSummaryNotes}
              </DataRow>
            </>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Technical Details'>
        <SectionContent>
          <DataRow>
            <Text strong>Inspection Surface:</Text>{' '}
            {dataSheetSectionData.inspectionSurface || 'Not specified'}
          </DataRow>
          <DataRow>
            <Text strong>Surface Condition:</Text>{' '}
            {dataSheetSectionData.surfaceCondition || 'Not specified'}
          </DataRow>
          <DataRow>
            <Text strong>Coating Present:</Text>{' '}
            {dataSheetSectionData.coatingPresent ? 'Yes' : 'No'}
          </DataRow>

          {/* Display robot information */}
          {dataSheetSectionData.robots &&
            dataSheetSectionData.robots.length > 0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Robots Used:</Text>
                </DataRow>
                {dataSheetSectionData.robots.map((robot, index) => (
                  <DataRow key={index} style={{ marginLeft: 16 }}>
                    Robot {index + 1}: {robot.model || 'Unknown Model'} (S/N:{' '}
                    {robot.serialNumber})
                  </DataRow>
                ))}
              </>
            )}

          {/* Display calibration blocks */}
          {dataSheetSectionData.calibrationBlocks &&
            dataSheetSectionData.calibrationBlocks.length > 0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Calibration Blocks:</Text>
                </DataRow>
                {dataSheetSectionData.calibrationBlocks.map((block, idx) => (
                  <DataRow key={idx} style={{ marginLeft: 16 }}>
                    {block.type || 'N/A'} - S/N: {block.serialNumber || 'N/A'}
                  </DataRow>
                ))}
              </>
            )}

          {/* Display calibration times */}
          {dataSheetSectionData.calibrationTimes &&
            dataSheetSectionData.calibrationTimes.length > 0 && (
              <DataRow style={{ marginTop: 8, marginLeft: 16 }}>
                Calibration Times:{' '}
                {dataSheetSectionData.calibrationTimes
                  .map(formatCalibrationTime)
                  .join(', ')}
              </DataRow>
            )}

          {/* Display calibration steps */}
          {dataSheetSectionData.calibrationSteps &&
            dataSheetSectionData.calibrationSteps.length > 0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Calibration Steps:</Text>
                </DataRow>
                {dataSheetSectionData.calibrationSteps.map((step, index) => (
                  <DataRow key={index} style={{ marginLeft: 16 }}>
                    {step.stepName}: {step.distance.value} {step.distance.unit}
                  </DataRow>
                ))}
              </>
            )}

          {/* Display sensor serial numbers */}
          {dataSheetSectionData.sensorSerialNumbers &&
            Object.keys(dataSheetSectionData.sensorSerialNumbers).length >
              0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Sensor Serial Numbers:</Text>
                </DataRow>
                {Object.entries(dataSheetSectionData.sensorSerialNumbers).map(
                  ([key, serials]) => (
                    <DataRow key={key} style={{ marginLeft: 16 }}>
                      Sensor {key}: {serials.join(', ')}
                    </DataRow>
                  )
                )}
              </>
            )}
        </SectionContent>
      </PageSection>

      <PageSection title='Metadata'>
        <SectionContent>
          {inspectionMetadata?.inspectionSoftwareVersion && (
            <DataRow>
              <Text strong>Inspection Software Version:</Text>{' '}
              {inspectionMetadata.inspectionSoftwareVersion}
            </DataRow>
          )}
          {inspectionMetadata?.reported_ascan_count !== undefined && (
            <DataRow>
              <Text strong>A-Scan Count:</Text>{' '}
              {inspectionMetadata.reported_ascan_count.toLocaleString()}
            </DataRow>
          )}
          {payloadTypesStr && (
            <DataRow>
              <Text strong>Sensor Payload Type:</Text> {payloadTypesStr}
            </DataRow>
          )}
          {sensorFrequenciesStr && (
            <DataRow>
              <Text strong>Sensor Frequency:</Text> {sensorFrequenciesStr}
            </DataRow>
          )}
          {sensorDiametersStr && (
            <DataRow>
              <Text strong>Sensor Diameter:</Text> {sensorDiametersStr}
            </DataRow>
          )}
          {sensorAngle !== undefined && (
            <DataRow>
              <Text strong>Sensor Angle:</Text> {sensorAngle}°
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Selected Images'>
        <SectionContent>
          {imagesSectionData.imageReportIds &&
          imagesSectionData.imageReportIds.length > 0 ? (
            <DataRow>
              <Text strong>Number of Images:</Text>{' '}
              {imagesSectionData.imageReportIds.length}
            </DataRow>
          ) : (
            <DataRow>
              <Text type='secondary'>No images selected</Text>
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Review Roles'>
        <SectionContent>
          <DataRow>
            <Text type='secondary'>
              Record reviewer details here. The exported PDF is signed manually
              outside Forecast.
            </Text>
          </DataRow>
          {!previewMode && hasSavedReviewEntries && !isEditingReviewers && (
            <Space style={{ marginTop: 16 }}>
              <Button type='primary' onClick={startEditingReviewers}>
                Edit Reviewers
              </Button>
            </Space>
          )}
          <ReviewForm form={form} layout='vertical'>
            <div style={{ marginTop: 16 }}>
              {REVIEW_ROLE_ORDER.map((reviewRole) => {
                const reviewEntry = getReviewEntryForRole(
                  signoff,
                  reviewRole.role
                )
                const signDate =
                  reviewRole.role === SignoffRole.SUPERVISOR
                    ? supervisorSignDate
                    : reviewRole.role === SignoffRole.DATA_ANALYST
                      ? dataAnalystSignDate
                      : reviewerSignDate

                return (
                  <div key={reviewRole.role} style={{ marginBottom: '16px' }}>
                    <Text strong>{reviewRole.label}</Text>
                    {showReviewEditor ? (
                      <>
                        <Form.Item
                          name={[reviewRole.field, 'name']}
                          label='Full Name'
                          rules={
                            isReviewRoleRequired(reviewRole.role)
                              ? [{ required: true }]
                              : []
                          }>
                          <Input disabled={isLoading} />
                        </Form.Item>

                        <Form.Item
                          name={[reviewRole.field, 'qualifications']}
                          label='Qualifications'
                          rules={
                            isReviewRoleRequired(reviewRole.role)
                              ? [{ required: true }]
                              : []
                          }>
                          <Select
                            options={QualificationOptions.map((value) => ({
                              value,
                              label: value,
                            }))}
                            disabled={isLoading}
                          />
                        </Form.Item>
                      </>
                    ) : (
                      <>
                        <DataRow>
                          <Text>Name:</Text>{' '}
                          {reviewEntry?.name || 'Not specified'}
                        </DataRow>
                        <DataRow>
                          <Text>Qualifications:</Text>{' '}
                          {reviewEntry?.qualifications || 'Not specified'}
                        </DataRow>
                        <DataRow>
                          <Text>Last Updated:</Text> {formatDate(signDate)}
                        </DataRow>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
            {showReviewEditor && (
              <Space>
                <Button type='primary' onClick={handleSaveReviewers}>
                  Save Review Details
                </Button>
                {hasSavedReviewEntries && (
                  <Button onClick={stopEditingReviewers}>Cancel Editing</Button>
                )}
              </Space>
            )}
          </ReviewForm>
        </SectionContent>
      </PageSection>

      {previewMode && signoff && (
        <PageSection title='Review Details'>
          <SectionContent>
            {signoff.supervisorSignoff && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>{getRoleLabel(SignoffRole.SUPERVISOR)}</Text>
                <DataRow>
                  <Text>Name:</Text> {signoff.supervisorSignoff.name}
                </DataRow>
                <DataRow>
                  <Text>Qualifications:</Text>{' '}
                  {signoff.supervisorSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text>Date:</Text>{' '}
                  {format(
                    new Date(signoff.supervisorSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </div>
            )}
            {signoff.dataAnalystSignoff && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>{getRoleLabel(SignoffRole.DATA_ANALYST)}</Text>
                <DataRow>
                  <Text>Name:</Text> {signoff.dataAnalystSignoff.name}
                </DataRow>
                <DataRow>
                  <Text>Qualifications:</Text>{' '}
                  {signoff.dataAnalystSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text>Date:</Text>{' '}
                  {format(
                    new Date(signoff.dataAnalystSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </div>
            )}
            {signoff.reviewerSignoff && (
              <div>
                <Text strong>{getRoleLabel(SignoffRole.REVIEWER)}</Text>
                <DataRow>
                  <Text>Name:</Text> {signoff.reviewerSignoff.name}
                </DataRow>
                <DataRow>
                  <Text>Qualifications:</Text>{' '}
                  {signoff.reviewerSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text>Date:</Text>{' '}
                  {format(
                    new Date(signoff.reviewerSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </div>
            )}
          </SectionContent>
        </PageSection>
      )}
    </div>
  )
}

export const ReportReview: ReviewSection = {
  id: RugReportSectionId.REVIEW,
  title: 'Review',
  description: 'Review report data and complete sign off',
  RenderReact,
  required: true,
}
